import React from "react";
import {
  ChartPieIcon,
  TrophyIcon,
  UsersIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

interface TasteProfile {
  goldenEra: number;
  modern: number;
  underground: number;
  mainstream: number;
}

interface FullTasteProfile {
  totalArtistsConsidered: number;
  totalWeight: number;
  topEras: Array<{ value: string; weight: number; percentage: number }>;
  topRegions: Array<{ value: string; weight: number; percentage: number }>;
  topStyles: Array<{ value: string; weight: number; percentage: number }>;
  topTags: Array<{ value: string; weight: number; percentage: number }>;
  numericScores: {
    mainstreamLevel: number | null;
    lyricismLevel: number | null;
    influenceLevel: number | null;
  };
  summary: {
    lyricalVsMainstream: "lyrical" | "balanced" | "mainstream";
    eraLean: string | null;
    regionLean: string | null;
    explorerScore: number;
    profileLabel: string;
  };
}

interface TasteProfileCardProps {
  tasteProfile: TasteProfile;
  communityMatch: number;
  topGenres: string[];
  totalLists: number;
  fullProfile?: FullTasteProfile | null;
  message?: string;
}

const TasteProfileCard: React.FC<TasteProfileCardProps> = ({
  tasteProfile,
  communityMatch,
  topGenres,
  totalLists,
  fullProfile,
  message,
}) => {
  // If no data available, show empty state
  if (!fullProfile && totalLists === 0) {
    return (
      <div className="card-wrap">
        <div className="flex items-center mb-6">
          <ChartPieIcon className="w-6 h-6 text-blue-400 mr-2" />
          <h3 className="text-xl font-bold text-white">Taste Profile</h3>
        </div>

        <div className="text-center py-8">
          <SparklesIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-sm">
            {message ||
              "Create some ranked lists to generate your taste profile!"}
          </p>
        </div>
      </div>
    );
  }
  // Calculate percentages
  const total =
    tasteProfile.goldenEra +
    tasteProfile.modern +
    tasteProfile.underground +
    tasteProfile.mainstream;
  const percentages = {
    goldenEra: total > 0 ? (tasteProfile.goldenEra / total) * 100 : 0,
    modern: total > 0 ? (tasteProfile.modern / total) * 100 : 0,
    underground: total > 0 ? (tasteProfile.underground / total) * 100 : 0,
    mainstream: total > 0 ? (tasteProfile.mainstream / total) * 100 : 0,
  };

  // Simple CSS-based donut chart using conic-gradient
  const donutStyle = {
    background: `conic-gradient(
      from 0deg,
      #f59e0b ${percentages.goldenEra}%,
      #10b981 ${percentages.goldenEra}% ${percentages.goldenEra + percentages.modern}%,
      #8b5cf6 ${percentages.goldenEra + percentages.modern}% ${percentages.goldenEra + percentages.modern + percentages.underground}%,
      #ef4444 ${percentages.goldenEra + percentages.modern + percentages.underground}% 100%
    )`,
  };

  const tasteData = [
    { name: "Golden Era", value: percentages.goldenEra, color: "#f59e0b" },
    { name: "Modern", value: percentages.modern, color: "#10b981" },
    { name: "Underground", value: percentages.underground, color: "#8b5cf6" },
    { name: "Mainstream", value: percentages.mainstream, color: "#ef4444" },
  ];

  return (
    <div className="card-wrap">
      <div className="flex items-center mb-6">
        <ChartPieIcon className="w-6 h-6 text-blue-400 mr-2" />
        <h3 className="text-xl font-bold text-white">Taste Profile</h3>
      </div>

      {/* Enhanced Profile Information */}
      {fullProfile && (
        <>
          {/* Profile Label */}
          <div className="mb-4 text-center">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30">
              <SparklesIcon className="w-4 h-4 text-blue-400 mr-1" />
              <span className="text-sm font-medium text-blue-300">
                {fullProfile.summary.profileLabel}
              </span>
            </div>
          </div>

          {/* Top Characteristics */}
          <div className="grid grid-cols-1 gap-3 mb-4">
            {/* Era Preference */}
            {fullProfile.summary.eraLean && (
              <div className="flex justify-between items-center px-3 py-2 bg-gray-800/50 rounded-lg">
                <span className="text-sm text-gray-300">Era Focus</span>
                <span className="text-sm font-medium text-white capitalize">
                  {fullProfile.summary.eraLean}
                </span>
              </div>
            )}

            {/* Regional Preference */}
            {fullProfile.summary.regionLean && (
              <div className="flex justify-between items-center px-3 py-2 bg-gray-800/50 rounded-lg">
                <span className="text-sm text-gray-300">Region</span>
                <span className="text-sm font-medium text-white capitalize">
                  {fullProfile.summary.regionLean.replace("_", " ")}
                </span>
              </div>
            )}

            {/* Lyrical vs Mainstream */}
            <div className="flex justify-between items-center px-3 py-2 bg-gray-800/50 rounded-lg">
              <span className="text-sm text-gray-300">Style</span>
              <span className="text-sm font-medium text-white capitalize">
                {fullProfile.summary.lyricalVsMainstream}
              </span>
            </div>

            {/* Explorer Score */}
            <div className="flex justify-between items-center px-3 py-2 bg-gray-800/50 rounded-lg">
              <span className="text-sm text-gray-300">Diversity Score</span>
              <span className="text-sm font-medium text-white">
                {fullProfile.summary.explorerScore}/10
              </span>
            </div>
          </div>
        </>
      )}

      {/* Community Match */}
      <div className="flex flex-row justify-evenly items-center mb-6">
        <div className="relative">
          <div className="w-32 h-32 rounded-full" style={donutStyle}>
            <div className="absolute inset-4 bg-gray-800 rounded-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">
                  {totalLists}
                </div>
                <div className="text-xs text-gray-400">Lists</div>
              </div>
            </div>
          </div>
        </div>
        <div className="relative">
          <div className="w-32 h-32 rounded-full" style={donutStyle}>
            <div className="absolute inset-4 bg-gray-800 rounded-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">
                  {fullProfile?.totalArtistsConsidered || totalLists}
                </div>
                <div className="text-xs text-gray-400">
                  {fullProfile ? "Artists" : "Lists"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-center mb-6">
        {/* Legend */}
        <div className="grid grid-cols-2 gap-2 mt-4 w-full">
          {tasteData.map((item) => (
            <div key={item.name} className="flex items-center">
              <div
                className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{item.name}</div>
                <div className="text-xs text-gray-400">
                  {item.value.toFixed(1)}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Donut Chart */}
      {/* <div className="flex flex-col items-center mb-6">
        <div className="relative">
          <div className="w-32 h-32 rounded-full" style={donutStyle}>
            <div className="absolute inset-4 bg-gray-800 rounded-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">
                  {fullProfile?.totalArtistsConsidered || totalLists}
                </div>
                <div className="text-xs text-gray-400">
                  {fullProfile ? "Artists" : "Lists"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Community Match */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <UsersIcon className="w-4 h-4 text-green-400 mr-1" />
            <span className="text-sm text-gray-300">Community Match</span>
          </div>
          <span className="text-sm font-semibold text-green-400">
            {communityMatch}%
          </span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-green-500 to-green-400 h-2 rounded-full transition-all duration-500"
            style={{ width: `${communityMatch}%` }}
          />
        </div>
      </div>

      {/* Top Genres */}
      <div>
        <div className="flex items-center mb-3">
          <TrophyIcon className="w-4 h-4 text-yellow-400 mr-1" />
          <span className="text-sm text-gray-300">Top Genres</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {topGenres.slice(0, 4).map((genre, index) => (
            <span
              key={genre}
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                index === 0
                  ? "bg-yellow-500 text-black"
                  : index === 1
                    ? "bg-gray-600 text-white"
                    : index === 2
                      ? "bg-amber-600 text-white"
                      : "bg-gray-700 text-gray-300"
              }`}
            >
              {genre}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TasteProfileCard;
